import React, {
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { MEDIA_VARIANTS } from '../../../media';
import Button from '../../../ui/Button';
import ProgressBar from '../../../ui/ProgressBar';
import { ImageLoader } from '../ImageLoader';
import {
  formatBuildDuration,
  formatBuildingRemaining,
  getCampTravelAvailability,
  getBuildingProgress,
  isBuildingFunded,
} from './baseCamp';
import './BaseCamp.css';

const STATUS_LABELS = {
  unbuilt: 'Needs materials',
  readyToBuild: 'Ready to build',
  building: 'Building',
  completed: 'Completed',
  locked: 'Locked',
};
const EMPTY_BUILDINGS = [];

function currentBuildingLevel(building) {
  const configured = Number(building?.currentLevel);
  if (Number.isFinite(configured)) return Math.max(0, Math.floor(configured));
  return building?.status === 'completed' ? 1 : 0;
}

function maximumBuildingLevel(building) {
  const currentLevel = currentBuildingLevel(building);
  const configured = Number(building?.maxLevel);
  return Number.isFinite(configured)
    ? Math.max(currentLevel, Math.floor(configured))
    : Math.max(1, currentLevel);
}

function buildingStatusLabel(building) {
  const currentLevel = currentBuildingLevel(building);
  const maxLevel = maximumBuildingLevel(building);
  if (building.status === 'building' && currentLevel > 0) {
    return `Upgrading to level ${building.targetLevel || currentLevel + 1}`;
  }
  if (
    currentLevel > 0
    && maxLevel > 0
    && currentLevel >= maxLevel
  ) {
    return 'Maximum level';
  }
  if (currentLevel > 0 && building.status === 'completed') {
    return 'Upgrade available';
  }
  return STATUS_LABELS[building.status] || 'Unavailable';
}

function availableForCost(cost) {
  return Number(cost.availableInPack || 0)
    + Number(cost.availableInStorage || 0);
}

function hasDepositableMaterials(building) {
  return (building.costs || []).some(cost => (
    cost.contributed < cost.required
    && availableForCost(cost) > 0
  ));
}

function getProfessionRequirement(building, professions) {
  const buildingLevel = currentBuildingLevel(building);
  if (buildingLevel >= maximumBuildingLevel(building)) return null;
  const targetLevel = Math.max(
    1,
    Number(building?.targetLevel) || buildingLevel + 1,
  );
  const professionKey = String(building?.professionKey || '');
  if (!professionKey || targetLevel <= 1) return null;

  const profession = (professions || []).find(entry => (
    entry.professionKey === professionKey
  ));
  const currentLevel = Math.max(1, Number(profession?.level) || 1);
  return {
    currentLevel,
    met: currentLevel >= targetLevel,
    name: profession?.name || professionKey,
    targetLevel,
  };
}

function ProjectCosts({ building }) {
  if (!building.costs?.length) {
    return (
      <p className="camp-project__state">
        No material requirements are available for this project.
      </p>
    );
  }

  return (
    <ul aria-label={`${building.name} material requirements`} className="camp-costs">
      {building.costs.map(cost => {
        const required = Math.max(0, Number(cost.required) || 0);
        const contributed = Math.min(
          required,
          Math.max(0, Number(cost.contributed) || 0),
        );
        const percent = required > 0
          ? (contributed / required) * 100
          : 0;
        return (
          <li className="camp-cost" key={cost.itemId}>
            <div className="camp-cost__heading">
              <strong>{cost.itemName}</strong>
              <span>{contributed} / {required}</span>
            </div>
            <ProgressBar
              label={`${cost.itemName}: ${contributed} of ${required} contributed`}
              value={percent}
              variant="xp"
            />
            <span className="camp-cost__available">
              Available: {cost.availableInPack} Pack · {cost.availableInStorage} Vault
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function ProjectAction({
  building,
  busy,
  canUseCamp,
  onCancel,
  onDeposit,
  onStart,
  professionRequirement,
}) {
  const currentLevel = currentBuildingLevel(building);
  const hasNextLevel = currentLevel < maximumBuildingLevel(building);
  const professionLocked = professionRequirement?.met === false;
  if (building.status === 'completed' && !hasNextLevel) {
    return <p className="camp-project__complete">This Camp service is ready.</p>;
  }

  if (building.status === 'building') {
    return (
      <Button
        disabled={busy || !canUseCamp}
        onClick={() => onCancel(building)}
        variant="secondary"
      >
        {busy ? 'Cancelling…' : 'Cancel construction'}
      </Button>
    );
  }

  if (building.status === 'locked') {
    return (
      <p className="camp-project__state">
        {building.lockedReason || 'Complete an earlier Camp project first.'}
      </p>
    );
  }

  if (building.status === 'completed' && !building.costs?.length) {
    return (
      <p className="camp-project__state">
        The next upgrade is not available yet.
      </p>
    );
  }

  const funded = building.status === 'readyToBuild'
    || isBuildingFunded(building);
  if (funded) {
    return (
      <Button
        disabled={busy || !canUseCamp || professionLocked}
        onClick={() => onStart(building)}
        title={professionLocked
          ? `${professionRequirement.name} level ${professionRequirement.targetLevel} is required.`
          : undefined}
        variant="primary"
      >
        {busy
          ? 'Starting…'
          : currentLevel > 0
            ? `Start level ${building.targetLevel || currentLevel + 1} upgrade`
            : 'Start construction'}
      </Button>
    );
  }

  const canDeposit = hasDepositableMaterials(building);
  return (
    <>
      <Button
        disabled={busy || !canUseCamp || !canDeposit}
        onClick={() => onDeposit(building)}
        variant="primary"
      >
        {busy
          ? 'Depositing…'
          : currentLevel > 0
            ? 'Deposit toward upgrade'
            : 'Deposit available'}
      </Button>
      {!canDeposit ? (
        <p className="camp-project__state">
          Gather a missing material before depositing.
        </p>
      ) : null}
    </>
  );
}

export default function CampProjectsPanel({
  baseCamp,
  busyBuildingKey = '',
  campEntrance = null,
  currentAction = '',
  embedded = false,
  error = '',
  hasCombat = false,
  loading = false,
  onDeposit,
  onCancel,
  onEnterCamp,
  onExitCamp,
  onOpenStorage,
  onRefresh,
  onStart,
  playerPosition = null,
  travelBusy = false,
}) {
  const componentId = useId().replace(/:/g, '');
  const travelHintId = `camp-travel-hint-${componentId}`;
  const [now, setNow] = useState(() => Date.now());
  const refreshedCompletionsRef = useRef(new Set());
  const buildings = baseCamp?.buildings || EMPTY_BUILDINGS;
  const hasActiveBuild = buildings.some(building => (
    building.status === 'building'
  ));

  useEffect(() => {
    if (!hasActiveBuild) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [hasActiveBuild]);

  const serverNow = now + Number(baseCamp?.serverTimeOffsetMs || 0);
  useEffect(() => {
    if (typeof onRefresh !== 'function') return;
    buildings.forEach(building => {
      if (building.status !== 'building') return;
      const completesAtMs = Date.parse(building.completesAtUtc);
      if (!Number.isFinite(completesAtMs) || serverNow < completesAtMs) {
        return;
      }
      const completionKey = [
        baseCamp?.baseId || '',
        building.buildingKey,
        building.completesAtUtc,
      ].join(':');
      if (refreshedCompletionsRef.current.has(completionKey)) return;
      refreshedCompletionsRef.current.add(completionKey);
      try {
        Promise.resolve(onRefresh()).catch(refreshError => {
          console.error(
            'Failed to refresh Camp after construction elapsed',
            refreshError,
          );
        });
      } catch (refreshError) {
        console.error(
          'Failed to refresh Camp after construction elapsed',
          refreshError,
        );
      }
    });
  }, [baseCamp?.baseId, buildings, onRefresh, serverNow]);

  const travelAvailability = getCampTravelAvailability({
    currentAction,
    entrance: campEntrance,
    hasCombat,
    isInCamp: baseCamp?.isInCamp,
    playerPosition,
  });

  if (loading && !baseCamp) {
    return (
      <section
        aria-busy="true"
        aria-label="Camp projects"
        className="camp-projects camp-projects--state"
      >
        <p role="status">Loading Camp projects…</p>
      </section>
    );
  }

  if (error && !baseCamp) {
    return (
      <section
        aria-label="Camp projects"
        className="camp-projects camp-projects--state"
      >
        <p className="camp-projects__error" role="alert">{error}</p>
        <Button onClick={onRefresh} variant="primary">Try again</Button>
      </section>
    );
  }

  if (!baseCamp || buildings.length === 0) {
    return (
      <section
        aria-label="Camp projects"
        className="camp-projects camp-projects--state"
      >
        {error ? (
          <p className="camp-projects__error" role="alert">{error}</p>
        ) : (
          <p>No Camp projects are available yet.</p>
        )}
        {error ? (
          <Button disabled={loading} onClick={onRefresh} variant="primary">
            {loading ? 'Trying again…' : 'Try again'}
          </Button>
        ) : null}
      </section>
    );
  }

  return (
    <section
      aria-busy={loading || travelBusy || undefined}
      aria-label="Camp projects"
      className={`camp-projects${embedded ? ' camp-projects--embedded' : ''}`}
    >
      {!embedded ? (
        <div className="camp-projects__toolbar">
          <div className="camp-projects__header-actions">
            <Button
              aria-describedby={travelHintId}
              disabled={travelBusy || !travelAvailability.allowed}
              onClick={() => {
                if (travelBusy || !travelAvailability.allowed) return;
                if (baseCamp.isInCamp) onExitCamp?.();
                else onEnterCamp?.();
              }}
              variant="primary"
            >
              {travelBusy
                ? 'Travelling…'
                : baseCamp.isInCamp
                  ? 'Leave Camp'
                  : 'Enter Camp'}
            </Button>
            <Button
              disabled={
                travelBusy
                || !baseCamp.isInCamp
                || !baseCamp.storage?.unlocked
              }
              onClick={onOpenStorage}
              variant="secondary"
            >
              Open Vault
            </Button>
          </div>
        </div>
      ) : null}

      {!embedded ? (
        <p
          className="camp-projects__notice"
          id={travelHintId}
          role="status"
        >
          {travelBusy ? 'Camp travel is in progress…' : travelAvailability.reason}
        </p>
      ) : null}
      {!baseCamp.storage?.unlocked ? (
        <p className="camp-projects__notice">
          Restore the Found Vault to unlock Camp storage.
        </p>
      ) : null}
      {error ? <p className="camp-projects__error" role="alert">{error}</p> : null}

      <div className="camp-projects__list">
        {buildings.map(building => {
          const currentLevel = currentBuildingLevel(building);
          const maxLevel = maximumBuildingLevel(building);
          const busy = busyBuildingKey === building.buildingKey;
          const progress = getBuildingProgress(building, serverNow);
          const professionRequirement = getProfessionRequirement(
            building,
            baseCamp.professions,
          );
          const projectTitleId = [
            'camp-project',
            componentId,
            building.buildingKey,
          ].join('-');
          return (
            <article
              aria-labelledby={projectTitleId}
              className={`camp-project camp-project--${building.status}`}
              key={building.buildingKey}
            >
              <header className="camp-project__header">
                <h3 id={projectTitleId}>{building.name}</h3>
              </header>

              <div className="camp-project__intro">
                <ImageLoader
                  alt=""
                  className="camp-project__image"
                  imageId={building.imageId}
                  variant={MEDIA_VARIANTS.CARD}
                />
                <div className="camp-project__summary">
                  <p className="camp-project__description">
                    {building.description || 'No project description available.'}
                  </p>
                  <span className="camp-project__status">
                    {buildingStatusLabel(building)}
                  </span>
                </div>
              </div>

              <dl className="camp-project__meta">
                <div>
                  <dt>Level</dt>
                  <dd>{currentLevel}{maxLevel > 0 ? ` / ${maxLevel}` : ''}</dd>
                </div>
                <div>
                  <dt>Build time</dt>
                  <dd>
                    {building.buildDurationSeconds > 0
                      ? formatBuildDuration(building.buildDurationSeconds)
                      : '—'}
                  </dd>
                </div>
              </dl>

              <div className="camp-project__body">
                {building.recipeItemLevelCap > 0 ? (
                  <p className="camp-project__state">
                    Recipes up to item level {building.recipeItemLevelCap}
                  </p>
                ) : null}

                {professionRequirement ? (
                  <p className="camp-project__state">
                    {`Requires ${professionRequirement.name} level ${professionRequirement.targetLevel}${professionRequirement.met
                      ? '.'
                      : ` (current: ${professionRequirement.currentLevel}).`}`}
                  </p>
                ) : null}

                <ProjectCosts building={building} />

                {building.status === 'building' ? (
                  <div className="camp-project__construction">
                    <ProgressBar
                      label={`${building.name} construction progress`}
                      value={progress}
                      variant="xp"
                    />
                    <span aria-live="polite">
                      {formatBuildingRemaining(building, serverNow)}
                    </span>
                    {currentLevel > 0 ? (
                      <span>
                        Level {currentLevel} remains usable during this upgrade.
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="camp-project__actions">
                <ProjectAction
                  building={building}
                  busy={busy}
                  canUseCamp={baseCamp.isInCamp && !travelBusy}
                  onCancel={onCancel}
                  onDeposit={onDeposit}
                  onStart={onStart}
                  professionRequirement={professionRequirement}
                />
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
